import {
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  OnQuoteOfferingChange,
  PriceUpliftConfigFormSchema,
  PriceUpliftConfigFormType,
  PriceUpliftConfigTypeEnum,
  PriceUpliftPercentageTypeEnum,
  QuoteOfferingForUplift,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import {
  useCreatePriceUpliftConfig,
  useDeletePriceUpliftConfig,
  useGetPriceUpliftConfig,
  useUpdatePriceUpliftConfig,
} from '../../api/priceUpliftService';
import { ONE_TIME_PRODUCT_TYPES } from '../../constants/products';
import { logger } from '../../services/logger';
import { applyUpliftToItem } from '../../utils/upliftPrice.utils';
import {
  MButton,
  MDivider,
  MFlex,
  MFormField,
  MIcon,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MStack,
  MSwitch,
  MText,
  MTooltip,
} from '../Monetize';
import { PriceUpliftGrid } from './PriceUpliftGrid';

type PriceUpliftOnRenewalFormType = {
  current: PriceUpliftConfigFormType;
};

type PriceUpliftOnRenewalProps = {
  isReadOnly: boolean;
  quote: IQuoteRespSchema;
  quoteOffering: IQuoteOfferingRespSchema;
  quoteOfferingForUplift: QuoteOfferingForUplift;
  offeringRate: IRateResSchema;
  quotePrices: IQuotePrice[];
  onOfferingChange: OnQuoteOfferingChange;
};

export const PriceUpliftOnRenewal = ({
  isReadOnly,
  quote,
  quoteOffering,
  quoteOfferingForUplift,
  offeringRate,
  quotePrices,
  onOfferingChange,
}: PriceUpliftOnRenewalProps) => {
  const canUpdateAndDelete = !!quoteOffering.priceUpliftConfigurationId;
  const [canPriceUplift, setCanPriceUplift] = useState(
    !!quoteOffering.priceUpliftConfigurationId,
  );
  const [originalItems] = useState(() =>
    quoteOfferingForUplift?.items ? [...quoteOfferingForUplift.items] : [],
  );
  const [upliftedOffering, setUpliftedOffering] =
    useState<QuoteOfferingForUplift | null>(() =>
      quoteOfferingForUplift
        ? {
            ...quoteOfferingForUplift,
            items: [...(quoteOfferingForUplift.items || [])],
          }
        : null,
    );

  useEffect(() => {
    if (quoteOfferingForUplift) {
      setUpliftedOffering({
        ...quoteOfferingForUplift,
        items: [...(quoteOfferingForUplift.items || [])],
      });
    }
  }, [quoteOfferingForUplift]);

  const items = upliftedOffering?.items ?? quoteOfferingForUplift?.items ?? [];

  const handleUpliftForAllItems = (
    upliftPercentage: string,
    targetOffering: QuoteOfferingForUplift,
  ) => {
    if (!targetOffering || !originalItems.length) {
      return;
    }

    if (upliftPercentage === '') {
      const resetOffering = { ...targetOffering, items: [...originalItems] };
      setUpliftedOffering(resetOffering);
      return;
    }

    const upliftMultiplier = 1 + Number(upliftPercentage) / 100;

    const upliftedItems = originalItems.map((item) =>
      applyUpliftToItem(item, upliftMultiplier),
    );

    const newUpliftedOffering = { ...targetOffering, items: upliftedItems };
    setUpliftedOffering(newUpliftedOffering);
  };

  const handleUpliftForSingleItem = (
    upliftPercentage: string,
    targetOffering: QuoteOfferingForUplift,
    productId: string,
  ) => {
    if (!targetOffering || !originalItems.length || !upliftedOffering) {
      return;
    }

    const originalItem = originalItems.find(
      (item) => item.productId === productId,
    );

    if (!originalItem) {
      return;
    }

    const itemIndex = upliftedOffering.items.findIndex(
      (item) => item.productId === productId,
    );

    if (itemIndex === -1) {
      return;
    }

    const newItems = [...upliftedOffering.items];

    if (upliftPercentage === '') {
      newItems[itemIndex] = { ...originalItem };
    } else {
      const upliftMultiplier = 1 + Number(upliftPercentage) / 100;
      newItems[itemIndex] = applyUpliftToItem(originalItem, upliftMultiplier);
    }

    const newUpliftedOffering = { ...upliftedOffering, items: newItems };
    setUpliftedOffering(newUpliftedOffering);
  };

  const handleClearAllProductUplifts = (
    targetOffering: QuoteOfferingForUplift,
  ) => {
    if (!targetOffering || !originalItems.length) {
      return;
    }

    const resetOffering = { ...targetOffering, items: [...originalItems] };
    setUpliftedOffering(resetOffering);
  };

  const { isLoading: isPriceUpliftConfigLoading, data: priceUpliftConfig } =
    useGetPriceUpliftConfig(quoteOffering.priceUpliftConfigurationId ?? '', {
      enabled: !!quoteOffering.priceUpliftConfigurationId,
      refetchOnWindowFocus: false,
    });

  const isLocked = priceUpliftConfig?.locked ?? false;

  // Apply existing uplifts when priceUpliftConfig is loaded
  useEffect(() => {
    if (priceUpliftConfig && quoteOfferingForUplift) {
      if (
        priceUpliftConfig.type ===
        PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
      ) {
        handleUpliftForAllItems(
          priceUpliftConfig.amount.toString(),
          quoteOfferingForUplift,
        );
      } else if (
        priceUpliftConfig.type === PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC
      ) {
        const newItems = [...originalItems];

        priceUpliftConfig.productConfigurations?.forEach((config) => {
          if (config.amount !== null && config.amount !== undefined) {
            const itemIndex = newItems.findIndex(
              (item) => item.productId === config.productId,
            );

            if (itemIndex !== -1) {
              const upliftMultiplier = 1 + Number(config.amount) / 100;
              newItems[itemIndex] = applyUpliftToItem(
                originalItems[itemIndex],
                upliftMultiplier,
              );
            }
          }
        });

        const newUpliftedOffering = {
          ...quoteOfferingForUplift,
          items: newItems,
        };
        setUpliftedOffering(newUpliftedOffering);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceUpliftConfig, quoteOfferingForUplift, originalItems]);

  /** calculate values from priceUpliftConfig service */
  let values: PriceUpliftOnRenewalFormType | undefined;
  if (priceUpliftConfig) {
    values = {
      current: {
        amount: priceUpliftConfig.amount ?? null,
        type: priceUpliftConfig.type,
        percentageType:
          priceUpliftConfig.percentageType ??
          PriceUpliftPercentageTypeEnum.FIXED,
        productConfigurations: priceUpliftConfig.productConfigurations
          ? items.map(({ productId }) => {
              const savedConfig = priceUpliftConfig.productConfigurations?.find(
                (configuration) => configuration.productId === productId,
              );
              return {
                productId,
                percentageType:
                  savedConfig?.percentageType ??
                  priceUpliftConfig.percentageType,
                amount: savedConfig?.amount ?? null,
              };
            })
          : items.map(({ productId }) => ({
              productId,
              percentageType: priceUpliftConfig.percentageType,
              amount: null,
            })),
      },
    };
  }

  const {
    watch,
    control,
    formState: { isValid, isDirty },
    handleSubmit,
    setValue,
    reset,
  } = useForm<PriceUpliftOnRenewalFormType>({
    mode: 'onChange',
    values,
    defaultValues: {
      current: {
        amount: null,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
        type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
        productConfigurations: (quoteOfferingForUplift?.items ?? [])
          .filter(({ productType }) => !ONE_TIME_PRODUCT_TYPES.has(productType))
          .map(({ productId }) => ({
            amount: null,
            productId,
            percentageType: PriceUpliftPercentageTypeEnum.FIXED,
          })),
      },
    },
  });

  const resetUpliftForm = () =>
    reset({
      current: {
        amount: null,
        percentageType: PriceUpliftPercentageTypeEnum.FIXED,
        type: PriceUpliftConfigTypeEnum.PRODUCT_SPECIFIC,
        productConfigurations: items.map(({ productId }) => ({
          productId,
          percentageType: PriceUpliftPercentageTypeEnum.FIXED,
          amount: null,
        })),
      },
    });

  const { mutateAsync: doCreatePriceUpliftConfig, isPending: isCreateLoading } =
    useCreatePriceUpliftConfig({
      onSuccess: (data) => {
        onOfferingChange(
          'UPDATE',
          {
            ...quoteOffering,
            priceUpliftConfigurationId: data.id,
          } as IQuoteOfferingRespSchema,
          undefined,
          false,
          true,
        );
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });
  const { mutateAsync: doUpdatePriceUpliftConfig, isPending: isUpdateLoading } =
    useUpdatePriceUpliftConfig({
      onSuccess: () => {
        onOfferingChange('UPDATE', quoteOffering, undefined, false, true);
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const { mutate: doDeletePriceUpliftConfig, isPending: isDeleteLoading } =
    useDeletePriceUpliftConfig({
      meta: { showErrorToast: true },
      onSuccess: () => {
        resetUpliftForm();
        onOfferingChange(
          'UPDATE',
          {
            ...quoteOffering,
            priceUpliftConfigurationId: null,
          } as IQuoteOfferingRespSchema,
          undefined,
          false,
          true,
        );
      },
      // The configuration is still attached, so show it again with the edits
      // kept for a retry.
      onError: () => {
        setCanPriceUplift(true);
      },
    });

  const handlePriceUpliftToggle = (isEnabled: boolean) => {
    setCanPriceUplift(isEnabled);
    if (!isEnabled) {
      // A saved configuration is only reset once its delete succeeds.
      if (canUpdateAndDelete) {
        doDeletePriceUpliftConfig({
          quoteId: quoteOffering.quoteId ?? '',
          quoteOfferingId: quoteOffering.id ?? '',
          priceUpliftConfigurationId:
            quoteOffering.priceUpliftConfigurationId ?? '',
        });
      } else {
        resetUpliftForm();
      }
    }
  };

  const onSubmit = async (formData: PriceUpliftOnRenewalFormType) => {
    const renewalUpliftData = formData.current;

    const payload: PriceUpliftConfigFormType = {
      ...renewalUpliftData,
      productConfigurations:
        renewalUpliftData.type ===
        PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
          ? null
          : (renewalUpliftData.productConfigurations ?? [])
              .filter(({ amount }) => Boolean(amount))
              .map((config) => ({
                ...config,
                percentageType: renewalUpliftData.percentageType,
              })),
    };

    // Clearing every amount means no uplift: turn it off like the toggle does,
    // since an empty configuration still counts as an uplift in rules. With no
    // resolver the amounts are still raw strings here, so "" is the cleared one.
    const hasNoUplift =
      payload.type === PriceUpliftConfigTypeEnum.APPLY_TO_ALL_PRODUCTS
        ? !payload.amount
        : !payload.productConfigurations?.length;
    if (hasNoUplift) {
      handlePriceUpliftToggle(false);
      return;
    }

    const parsed = PriceUpliftConfigFormSchema.safeParse(payload);
    if (parsed.success) {
      const commonParams = {
        quoteOfferingId: quoteOffering.id ?? '',
        priceUpliftConfig: parsed.data,
      };

      if (!canUpdateAndDelete) {
        await doCreatePriceUpliftConfig({
          quoteId: quoteOffering.quoteId ?? '',
          ...commonParams,
        });
      } else if (!quoteOffering.priceUpliftConfigurationId) {
        logger.error(
          'priceUpliftConfigurationId is not available for updating.',
        );
      } else {
        await doUpdatePriceUpliftConfig({
          quoteId: quoteOffering.quoteId ?? '',
          priceUpliftConfigurationId: quoteOffering.priceUpliftConfigurationId,
          ...commonParams,
        });
      }
    } else {
      logger.error('Unable to parse data', parsed.error);
    }
  };

  const onError = (err: unknown) => logger.error(err);

  const percentageType = watch('current.percentageType');
  const isLockedOrIsReadOnly = isLocked || isReadOnly;

  if (isPriceUpliftConfigLoading) {
    return (
      <MFlex direction="column">
        <MSkeleton h={40} w="full" />
      </MFlex>
    );
  }

  return (
    <MFlex flexDir="column" gap={4}>
      <MFlex justify="space-between" w="full">
        <MText fontWeight="semibold" fontSize="md">
          Price Uplift on Renewal
        </MText>
        <MSwitch
          aria-label="Price uplift on renewal"
          isDisabled={isLockedOrIsReadOnly || isDeleteLoading}
          isReadOnly={isLockedOrIsReadOnly}
          isChecked={canPriceUplift}
          onChange={(ev) => handlePriceUpliftToggle(ev.target.checked)}
        />
      </MFlex>

      {canPriceUplift && (
        <MFlex
          as="form"
          id="price-uplift-config-form"
          flexDirection="column"
          gap={4}
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          <MText>
            Price increase can be automatically added to the renewal Quote. Set
            a percentage or a max limit for uplift pricing
          </MText>
          <MFormField>
            <Controller
              control={control}
              name="current.percentageType"
              render={({ field: { value, onChange, ...rest } }) => (
                <MRadioGroup
                  isDisabled={isLockedOrIsReadOnly}
                  onChange={onChange}
                  value={value}
                  {...rest}
                >
                  <MStack gap={4} direction="row" color="tGray.darkPurple">
                    <MRadio value={PriceUpliftPercentageTypeEnum.FIXED}>
                      Fixed Percentage
                    </MRadio>
                    <MRadio value={PriceUpliftPercentageTypeEnum.MAX}>
                      <MText display="inline-flex" alignItems="center">
                        Max Percentage
                        <MTooltip
                          label="Maximum percentage allowed for the price to increase"
                          placement="bottom-start"
                          shouldWrapChildren
                        >
                          <MIcon
                            display="flex"
                            alignItems="center"
                            as={MdInfo}
                            color="tPurple.base"
                            mx={1}
                          />
                        </MTooltip>
                      </MText>
                    </MRadio>
                  </MStack>
                </MRadioGroup>
              )}
            />
          </MFormField>
          <MDivider />
          <PriceUpliftGrid
            showUplift
            namePrefix="current"
            items={items}
            control={control as any}
            isLockedOrIsReadOnly={isLockedOrIsReadOnly}
            percentageType={percentageType}
            setValue={setValue as any}
            quote={quote}
            quoteOffering={upliftedOffering || quoteOfferingForUplift}
            quotePrices={quotePrices}
            offeringRate={offeringRate}
            calculateUpliftForAllItems={handleUpliftForAllItems}
            calculateUpliftForSingleItem={handleUpliftForSingleItem}
            clearProductUpliftsForSegment={handleClearAllProductUplifts}
          />
          <MFlex justify="flex-end" align="center">
            <MButton
              type="submit"
              variant="primary"
              isDisabled={
                !isValid ||
                !isDirty ||
                isLockedOrIsReadOnly ||
                isCreateLoading ||
                isUpdateLoading ||
                isDeleteLoading
              }
              isLoading={isCreateLoading || isUpdateLoading || isDeleteLoading}
            >
              Save
            </MButton>
          </MFlex>
        </MFlex>
      )}
    </MFlex>
  );
};
